<template>
  <div class="semi-circle-progress">
    <svg 
      :width="size" 
      :height="size / 2 + padding"
      :viewBox="`0 0 ${size} ${size / 2 + padding}`"
      class="progress-svg"
    >
      <!-- Crear cada anillo -->
      <g 
        v-for="(ring, index) in rings" 
        :key="index"
        class="ring-group"
      >
        <!-- Fondo del anillo (gris) -->
        <path
          :d="getArcPath(index, 100)"
          fill="none"
          :stroke="backgroundColor"
          :stroke-width="ringWidth"
          stroke-linecap="round"
        />
        
        <!-- Progreso del anillo (coloreado) -->
        <path
          :d="getArcPath(index, ring.value)"
          fill="none"
          :stroke="ring.color || defaultColors[index % defaultColors.length]"
          :stroke-width="ringWidth"
          stroke-linecap="round"
          class="progress-path"
          :class="{ 'animated': animated }"
        />
      </g>

      <!-- Etiquetas de texto (opcional) -->
      <g v-if="showLabels">
        <text
          v-for="(ring, index) in rings"
          :key="`label-${index}`"
          :x="size / 2"
          :y="size / 2 - getRadius(index) + 5"
          text-anchor="middle"
          :font-size="labelFontSize"
          :fill="ring.color || defaultColors[index % defaultColors.length]"
          font-weight="600"
          class="ring-label"
        >
          {{ ring.value }}{{ showPercentage ? '%' : '' }}
        </text>
      </g>

      <!-- Título central (opcional) -->
      <text
        v-if="centerTitle"
        :x="size / 2"
        :y="size / 2 + 10"
        text-anchor="middle"
        :font-size="centerTitleSize"
        fill="#333"
        font-weight="700"
      >
        {{ centerTitle }}
      </text>

      <!-- Subtítulo central (opcional) -->
      <text
        v-if="centerSubtitle"
        :x="size / 2"
        :y="size / 2 + 30"
        text-anchor="middle"
        :font-size="centerSubtitleSize"
        fill="#999"
      >
        {{ centerSubtitle }}
      </text>
    </svg>

    <!-- Leyenda (opcional) -->
    <div v-if="showLegend" class="legend">
      <div 
        v-for="(ring, index) in rings" 
        :key="`legend-${index}`"
        class="legend-item"
      >
        <span 
          class="legend-color"
          :style="{ backgroundColor: ring.color || defaultColors[index % defaultColors.length] }"
        ></span>
        <span class="legend-label">{{ ring.label }}</span>
        <span class="legend-value">{{ ring.value }}{{ showPercentage ? '%' : '' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SemiCircleProgress2',
  props: {
    rings: {
      type: Array,
      required: true,
      default: () => []
      // Estructura esperada:
      // [
      //   { label: 'Videos', value: 75, color: '#6b20c1' },
      //   { label: 'Actividades', value: 60, color: '#ff5e20' },
      //   { label: 'Exámenes', value: 85, color: '#28a745' }
      // ]
    },
    size: {
      type: Number,
      default: 400
    },
    ringWidth: {
      type: Number,
      default: 30
    },
    ringGap: {
      type: Number,
      default: 10
    },
    backgroundColor: {
      type: String,
      default: '#e0e0e0'
    },
    showLabels: {
      type: Boolean,
      default: true
    },
    showPercentage: {
      type: Boolean,
      default: true
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    labelFontSize: {
      type: Number,
      default: 14
    },
    centerTitle: {
      type: String,
      default: ''
    },
    centerSubtitle: {
      type: String,
      default: ''
    },
    centerTitleSize: {
      type: Number,
      default: 24
    },
    centerSubtitleSize: {
      type: Number,
      default: 14
    },
    animated: {
      type: Boolean,
      default: true
    },
    defaultColors: {
      type: Array,
      default: () => ['#6b20c1', '#ff5e20', '#ffc107', '#28a745', '#17a2b8', '#dc3545']
    }
  },
  computed: {
    padding() {
      return 20
    }
  },
  methods: {
    getRadius(ringIndex) {
      // Radio del anillo: más grande para el externo, más pequeño para el interno
      const maxRadius = (this.size / 2) - this.padding - (this.ringWidth / 2)
      const radiusStep = this.ringWidth + this.ringGap
      return maxRadius - (ringIndex * radiusStep)
    },
    
    getArcPath(ringIndex, percentage) {
      const radius = this.getRadius(ringIndex)
      const centerX = this.size / 2
      const centerY = this.size / 2
      
      // Ángulo en radianes (180 grados = π radianes)
      // 0% = -π (180°), 100% = 0 (0°)
      const startAngle = Math.PI  // 180 grados
      const endAngle = Math.PI - (Math.PI * (percentage / 100))  // De 180° a 0°
      
      // Calcular puntos de inicio y fin
      const startX = centerX + radius * Math.cos(startAngle)
      const startY = centerY - radius * Math.sin(startAngle)
      const endX = centerX + radius * Math.cos(endAngle)
      const endY = centerY - radius * Math.sin(endAngle)
      
      // Determinar si el arco es mayor a 180 grados
      const largeArcFlag = percentage > 50 ? 0 : 1
      
      // Crear el path del arco
      return `
        M ${startX} ${startY}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
      `
    }
  }
}
</script>

<style scoped>
.semi-circle-progress {
  display: inline-block;
  text-align: center;
}

.progress-svg {
  overflow: visible;
}

.progress-path {
  transition: stroke-dashoffset 0.3s ease;
}

.progress-path.animated {
  animation: drawProgress 1.5s ease-out forwards;
}

@keyframes drawProgress {
  from {
    stroke-dasharray: 1000;
    stroke-dashoffset: 1000;
  }
  to {
    stroke-dasharray: 1000;
    stroke-dashoffset: 0;
  }
}

.ring-label {
  transition: all 0.3s ease;
}

/* Leyenda */
.legend {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-label {
  flex: 1;
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.legend-value {
  font-size: 14px;
  color: #666;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 768px) {
  .legend {
    max-width: 100%;
  }
}
</style>